// The draw's schedule, as arithmetic. Nothing here measures anything: every
// screen watching the same lottery must run the same beats at the same
// instants, so no duration may depend on a device's layout.
//
// Ported from the dir-lottery mockup, with one deliberate change — see
// gutterMs below. Segment durations, the gap curve and the finale/crown
// lengths are the mockup's verbatim.
//
// DOM-free and pure, following the countdownFrom / createDraftSync convention
// that exists precisely so timing logic can be asserted on without a renderer.

const FUNNEL_MS = 200 // hopper mouth -> tube inlet
// Up the tube to the nozzle. Slower than the mockup's 230: the tube is the
// one stretch where the ball is a ball rather than a blur, and it is the only
// part of the path a viewer can actually follow.
const TUBE_MS = 400
// The pour, then a beat. The balls drop into the agitator when the clock hits
// zero; this window covers the pour *and* about a second of churn afterwards.
// The mockup's 500 was a warm-up for a hopper that was already full. Sized
// against hopperSim's POUR_MS: with the last ball released at 900ms and landing
// by ~1150, this leaves about two seconds of churn before anything is drawn out
// of the tank. Anything much shorter has the first pick leaving as the last
// ball arrives, which reads as a mistake rather than as a machine starting up.
const SPINUP_MS = 3200
const FINALE_MS = 900 // "last ball in the tank" rattle
const CROWN_MS = 1020 // crown -> done

// The climb from the nozzle down the rail to the slot. The mockup derived this
// from a getBoundingClientRect of the target, which made the length of the
// whole reveal a function of the viewport: a taller rail lands more flights
// against the clamp and the draw runs seconds longer, so twelve phones would
// drift apart and each would think it was right. Same intent — a farther slot
// takes longer — as a pure function of the pick.
//
// Rounded to whole milliseconds so every beat is an integer and tests can
// assert equality rather than chase a float tolerance. It costs at most half a
// millisecond of fidelity per flight.
const GUTTER_MIN_MS = 190
const GUTTER_SPAN_MS = 240

function gutterMs(pick: number, teamCount: number): number {
  return Math.round(GUTTER_MIN_MS + (GUTTER_SPAN_MS * (teamCount - pick)) / (teamCount - 1))
}

// The draw accelerates through the middle and brakes for the last three, which
// is what makes twelve balls read as a countdown rather than a list.
//
// Roughly 4x the mockup's gaps. Its numbers were tuned against a demo shell
// nobody was reading names off; in the room a landed pick needs long enough to
// be seen, said out loud, and reacted to before the next ball moves.
//
// This returns 270 for pick 1 as well as pick 2, and pick 1's value is never
// used — the finale replaces the gap with its own rattle. Left in place rather
// than special-cased, because it matches the mockup exactly and a reader
// comparing the two files should not have to wonder.
function gapAfter(pick: number): number {
  if (pick >= 5) return 450
  if (pick >= 3) return 600
  return 800
}

export interface LotteryBeat {
  pick: number
  startMs: number
  /** Duration of the first segment, not an offset. */
  funnelMs: number
  /** Duration of the second segment. */
  tubeMs: number
  landMs: number
}

export interface LotteryTimeline {
  /** Draw order: teamCount, teamCount-1, … 2, 1. Never sorted ascending. */
  beats: LotteryBeat[]
  /** The rattle starts. Pick 1 is still in the hopper. */
  finaleMs: number
  /** Pick 1 lands; the order is locked. Equals the last beat's landMs. */
  crownMs: number
  doneMs: number
  totalMs: number
}

export type LotteryPhaseName = 'pre' | 'spinup' | 'drawing' | 'finale' | 'crown' | 'done'
export type FlightSegment = 'funnel' | 'tube' | 'gutter'

export interface LotteryFlight {
  pick: number
  segment: FlightSegment
  /** Linear progress 0..1 within the segment. Easing belongs to the renderer. */
  t: number
}

export interface LotteryPhase {
  phase: LotteryPhaseName
  /** Picks already in the rail, in the order they landed (descending). */
  landed: number[]
  /** Picks still in the hopper. Excludes the one in flight. */
  remaining: number[]
  flight: LotteryFlight | null
  /** What the LED reads. Null means "- -" (pre/spinup) or "LOCK" (crown/done). */
  ledPick: number | null
}

export function lotteryTimeline(teamCount: number): LotteryTimeline {
  // The backend never reveals with fewer than two teams — run_lottery! nulls
  // lottery_at and cancels instead — so this is a programming error, not a
  // state the UI has to render. (n - 1 would also divide by zero at n = 1.)
  if (!Number.isInteger(teamCount) || teamCount < 2) {
    throw new RangeError(`lotteryTimeline needs at least 2 teams, got ${teamCount}`)
  }

  const beats: LotteryBeat[] = []
  let t = SPINUP_MS

  // N down to 2. Pick 1 is held back for the finale, which is the whole point
  // of drawing in reverse — the room finds out who has 1.01 last.
  for (let pick = teamCount; pick >= 2; pick--) {
    const landMs = t + FUNNEL_MS + TUBE_MS + gutterMs(pick, teamCount)
    beats.push({ pick, startMs: t, funnelMs: FUNNEL_MS, tubeMs: TUBE_MS, landMs })
    t = landMs + gapAfter(pick)
  }

  const finaleMs = t
  const lastStart = finaleMs + FINALE_MS
  // gutterMs(1, n) is 430 for every n — pick 1 always gets the longest climb.
  const crownMs = lastStart + FUNNEL_MS + TUBE_MS + gutterMs(1, teamCount)
  beats.push({ pick: 1, startMs: lastStart, funnelMs: FUNNEL_MS, tubeMs: TUBE_MS, landMs: crownMs })

  const doneMs = crownMs + CROWN_MS
  return { beats, finaleMs, crownMs, doneMs, totalMs: doneMs }
}

// Splits the elapsed time inside a beat across the three segments and returns
// *linear* progress. The mockup's easings stay in the flight layer: this module
// answers when, the renderer answers how.
function flightAt(elapsedMs: number, beat: LotteryBeat): LotteryFlight {
  const into = elapsedMs - beat.startMs
  if (into < beat.funnelMs) {
    return { pick: beat.pick, segment: 'funnel', t: into / beat.funnelMs }
  }
  const afterFunnel = into - beat.funnelMs
  if (afterFunnel < beat.tubeMs) {
    return { pick: beat.pick, segment: 'tube', t: afterFunnel / beat.tubeMs }
  }
  const gutter = beat.landMs - beat.startMs - beat.funnelMs - beat.tubeMs
  return { pick: beat.pick, segment: 'gutter', t: (afterFunnel - beat.tubeMs) / gutter }
}

/**
 * What is on screen at `elapsedMs`. Every interval is half-open, [start, end),
 * and the answer depends on nothing but the argument — which is what makes a
 * seek free: a latecomer opening the page five seconds in gets the same answer
 * as everyone else instead of restarting from the top.
 */
export function lotteryPhaseAt(elapsedMs: number, t: LotteryTimeline): LotteryPhase {
  const landed: number[] = []
  const remaining: number[] = []
  let flight: LotteryFlight | null = null
  let ledPick: number | null = null

  // beats are already in draw order, so landed and remaining come out in it.
  // A ball leaves the hopper when its beat *starts*, not when it lands — the
  // mockup splices it out of the tank up front — so `remaining` excludes the
  // one in flight and the three lists partition the picks exactly.
  for (const beat of t.beats) {
    if (elapsedMs >= beat.landMs) {
      landed.push(beat.pick)
    } else if (elapsedMs >= beat.startMs) {
      flight = flightAt(elapsedMs, beat)
    } else {
      remaining.push(beat.pick)
    }
    if (elapsedMs >= beat.startMs) ledPick = beat.pick
  }

  const phase: LotteryPhaseName =
    elapsedMs < 0
      ? 'pre'
      : elapsedMs < t.beats[0].startMs
        ? 'spinup'
        : elapsedMs < t.finaleMs
          ? 'drawing'
          : elapsedMs < t.crownMs
            ? 'finale'
            : elapsedMs < t.doneMs
              ? 'crown'
              : 'done'

  // The mockup sets the LED to "01" when the rattle starts, before the last
  // ball moves, and to "LOCK" at the crown.
  if (phase === 'pre' || phase === 'spinup' || phase === 'crown' || phase === 'done') {
    ledPick = null
  } else if (phase === 'finale') {
    ledPick = 1
  }

  return { phase, landed, remaining, flight, ledPick }
}
